import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { STATUS } from "@/constants";
import type {
  CreateDomainPayload,
  ImportDomainPayload,
} from "@/features/data-hub/model/domainForm";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

export type { CreateDomainPayload, ImportDomainPayload } from "@/features/data-hub/model/domainForm";

export type DomainSummary = {
  id: number;
  name: string;
  description: string | null;
  connection_id: number;
  created_at: string;
  updated_at: string;
};

export type DomainsState = {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  domains: DomainSummary[];
};

export const domainsInitialState: DomainsState = {
  status: STATUS.IDLE,
  error: null,
  domains: [],
};

function isDomainSummary(value: unknown): value is DomainSummary {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.name === "string" &&
    (typeof row.description === "string" || row.description === null) &&
    typeof row.connection_id === "number" &&
    typeof row.created_at === "string" &&
    typeof row.updated_at === "string"
  );
}

function parseDomainsResponse(data: unknown): DomainSummary[] | null {
  if (!Array.isArray(data)) {
    return null;
  }
  if (!data.every(isDomainSummary)) {
    return null;
  }
  return data;
}

export const createDomain = createAsyncThunk<
  DomainSummary,
  CreateDomainPayload,
  { rejectValue: string }
>("domains/create", async (payload, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/domains", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isDomainSummary(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (e) {
    const message = e instanceof Error ? e.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const importDomain = createAsyncThunk<
  DomainSummary,
  ImportDomainPayload,
  { rejectValue: string }
>("domains/import", async (payload, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/domains/import", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isDomainSummary(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (e) {
    const message = e instanceof Error ? e.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const fetchDomains = createAsyncThunk<
  DomainSummary[],
  void,
  { rejectValue: string }
>("domains/fetch", async (_, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/domains", { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(`Request failed (${res.status})`);
    }
    const data: unknown = await res.json();
    const domains = parseDomainsResponse(data);
    if (!domains) {
      return rejectWithValue("Unexpected response shape");
    }
    return domains;
  } catch (e) {
    const message = e instanceof Error ? e.message : "Request failed";
    return rejectWithValue(message);
  }
});

const domainsSlice = createSlice({
  name: "domains",
  initialState: domainsInitialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchDomains.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchDomains.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.domains = action.payload;
      })
      .addCase(fetchDomains.rejected, (state) => {
        state.status = STATUS.SUCCEEDED;
        state.domains = [];
        state.error = null;
      });
  },
});

export const domainsReducer = domainsSlice.reducer;
